import React from 'react'
import { useTranslation } from 'react-i18next'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { QuestionData, AnswerKeySettings, IdealAnswerItem } from '../types'
import Card from '../../OldPortalComponents/Card'
import saveQuesicon from '@/assets/New-images/icon-ques-saved.svg'
import RichMarkdownContent from '@/components/UI/RichMarkdownContent'
import { divideArray } from '../utils/dataTransformers'

interface QuestionDisplayProps {
  questionData: QuestionData
  questionNumber: number
  isQuestionBank?: boolean
  onClick?: (e: React.MouseEvent) => void
  groupTagLabelClick?: (data: QuestionData) => void
  children?: React.ReactNode
}

const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  questionData,
  questionNumber,
  isQuestionBank = false,
  onClick,
  groupTagLabelClick,
  children,
}) => {
  const { t } = useTranslation()

  const getTableDisplay = (
    data: any,
    answerKeySettings: AnswerKeySettings | undefined,
    noHeader?: boolean,
  ) => {
    let temp = typeof data === 'string' ? JSON.parse(data) : data
    if (
      answerKeySettings?.enable_column_header &&
      answerKeySettings.column_headers
    ) {
      temp?.forEach((row: any[], index: number) => {
        row.unshift(answerKeySettings.column_headers![index])
      })
    }

    return (
      <table className="quiz-table-answer-table">
        {!noHeader && (
          <thead>
            <tr>
              {answerKeySettings?.headers?.map(
                (column: string, idx: number) => (
                  <th key={idx} className="quiz-table-answer-cell">
                    {column}
                  </th>
                ),
              )}
            </tr>
          </thead>
        )}
        <tbody>
          {temp?.map((row: any[], rowIdx: number) => (
            <tr key={rowIdx}>
              {row.map((column: any, colIdx: number) => (
                <td key={colIdx} className="quiz-table-answer-cell">
                  {column}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  const getMatchDisplay = (data: any) => {
    return (
      <table className="quiz-table-answer-table">
        <thead>
          <tr>
            {questionData.answer_key_settings?.headers?.map(
              (column: string, idx: number) => (
                <th key={idx} className="quiz-table-answer-cell">
                  {column}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {data?.map((row: any, idx: number) => (
            <tr key={idx}>
              <td className="quiz-table-answer-cell">{row.term}</td>
              <td className="quiz-table-answer-cell">{row.definition}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  const getIdealAnswerItemsDisplay = () => {
    return (
      <ul>
        {questionData.ideal_answer_items?.map(
          (item: IdealAnswerItem, idx: number) => (
            <li key={idx}>{item.text}</li>
          ),
        )}
      </ul>
    )
  }

  const getNewTableDisplay = (
    data: IdealAnswerItem[] = [],
    answerKeySettings: AnswerKeySettings = {},
  ) => {
    const columnCount = answerKeySettings.column_count || 2
    const dividedData = divideArray(data, columnCount)

    return (
      <table className="quiz-table-answer-table">
        <thead>
          <tr>
            {answerKeySettings.headers?.map((column: string, idx: number) => (
              <th key={idx} className="quiz-table-answer-cell">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dividedData.map((row, rowIdx) => (
            <tr key={rowIdx}>
              <th className="quiz-table-header-cell">
                {answerKeySettings.column_headers?.[rowIdx] || ''}
              </th>
              {row.map((column, colIdx) => (
                <td key={colIdx} className="quiz-table-answer-cell">
                  {column.text}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <Card
      className="quiz-question-editor-card clickable-card"
      onClick={onClick}
    >
      <div className="space-around">
        {!isQuestionBank && questionData.quiz_question_id && (
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="auto"
            overlay={d => (
              <Popover id="popover-basic-" {...d}>
                <Popover.Content
                  dangerouslySetInnerHTML={{
                    __html: t('Question saved in knowledge base'),
                  }}
                />
              </Popover>
            )}
          >
            <div className="save-ques-toltip">
              <img src={saveQuesicon} alt="" />
            </div>
          </OverlayTrigger>
        )}

        {questionData.breadcrumbs && (
          <div
            className="group-tag"
            onClick={(e: React.MouseEvent) => {
              groupTagLabelClick?.(questionData)
              e.stopPropagation()
            }}
          >
            {questionData.breadcrumbs.length > 1
              ? questionData.breadcrumbs.map(b => b.name).join('/')
              : questionData.breadcrumbs[0]?.name}
          </div>
        )}

        <div className="display-question">
          <b>{`Question ${questionNumber + 1}`}</b>
          <br />
          <RichMarkdownContent contextText={questionData.text} />
        </div>

        {/* Text/Bullet Type */}
        {(questionData.type === 'text' || questionData.type === 'bullet') && (
          <div className="display-textinput">
            <div className="text-answer-field">
              {!questionData.ideal_answer ||
              questionData.ideal_answer === '' ? (
                'Text answer field'
              ) : (
                <RichMarkdownContent contextText={questionData.ideal_answer} />
              )}
            </div>
          </div>
        )}

        {/* New Bullet Type */}
        {questionData.type === 'newbullet' && (
          <div className="display-description">
            {!questionData.ideal_answer_items ||
            questionData.ideal_answer_items.length === 0
              ? 'Bullet answer field'
              : getIdealAnswerItemsDisplay()}
          </div>
        )}

        {/* Multiple Choice Types */}
        {(questionData.type === 'radio' ||
          questionData.type === 'checkbox') && (
          <div className="display-options">
            {questionData.options?.map((option, index) => (
              <div
                key={option.id + '_DO_' + index}
                className="option-display-field"
              >
                {questionData.type === 'radio' && (
                  <span className="radio-circle" />
                )}
                {questionData.type === 'checkbox' && (
                  <span className="checkbox-square" />
                )}
                <RichMarkdownContent contextText={option.text} />
              </div>
            ))}
          </div>
        )}

        {/* Description Type */}
        {questionData.type === 'description' && (
          <div className="display-description">
            {getTableDisplay(
              questionData.ideal_answer,
              questionData.answer_key_settings,
              true,
            )}
          </div>
        )}

        {/* Table Type */}
        {questionData.type === 'table' && (
          <div className="display-description">
            {getNewTableDisplay(
              questionData.ideal_answer_items,
              questionData.answer_key_settings,
            )}
          </div>
        )}

        {/* Essay Type */}
        {questionData.type === 'essay' && (
          <div className="display-description">
            {getNewTableDisplay(
              questionData.ideal_answer_items,
              questionData.answer_key_settings,
            )}
          </div>
        )}

        {/* Matching Type */}
        {questionData.type === 'matching' && (
          <div className="display-description">
            {getMatchDisplay(questionData.matches)}
          </div>
        )}

        {/* Group Type - render children (sub-questions) */}
        {questionData.type === 'group' && children}
      </div>
    </Card>
  )
}

export default QuestionDisplay
